import { NgZone, inject, signal } from '@angular/core';
import Uppy from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import { YChartsSentryUtils } from 'NewAngular/services/sentry/sentry-utils';
import { UrlsService } from 'NewAngular/services/core/urls.service';
import { AgentTaskStatus } from 'AgentApp/ts/interfaces/agent-task.interface';
import * as i0 from "@angular/core";
const DEFAULT_LOADING_TEXT = 'Understanding your request...';
const MOCK_AGENT_TASKS = [
    {
        id: '1',
        status: AgentTaskStatus.COMPLETE,
        title: 'Risk/Reward Scatterplot',
        completed_at: '02/20/2026, 12:39 PM',
    },
    {
        id: '2',
        status: AgentTaskStatus.COMPLETE,
        title: 'AAPL Growth Metrics Fund Chart',
        completed_at: '02/20/2026, 12:39 PM',
    },
    {
        id: '3',
        status: AgentTaskStatus.IN_PROGRESS,
        title: 'Building scenario analysis model',
    },
    {
        id: '4',
        status: AgentTaskStatus.IN_PROGRESS,
        title: 'Creating Fund Chart',
    },
    {
        id: '5',
        status: AgentTaskStatus.CANCELLED,
        title: 'Creating Watchlist',
    },
];
const MAX_FILE_SIZE_IN_BYTES = 33554433; // 32MB
export class AgentWebSocketService {
    constructor() {
        this._ngZone = inject(NgZone);
        this._urlsService = inject(UrlsService);
        this._socket = null;
        this._currentAssistantText = '';
        this._currentThinkingText = '';
        this.messages = signal([]);
        this.isStreaming = signal(false);
        this.isConnected = signal(false);
        this.isLoading = signal(false);
        this.isFileUploading = signal(false);
        this.currentLoadingText = signal(DEFAULT_LOADING_TEXT);
        this.agentSteps = signal([]);
        this.allAgentTasks = signal(MOCK_AGENT_TASKS);
        this.toolCallsToConfirm = signal([]);
        this._uppy = new Uppy({
            autoProceed: false,
            restrictions: {
                maxFileSize: MAX_FILE_SIZE_IN_BYTES,
                maxNumberOfFiles: 5,
                allowedFileTypes: ['image/*', '.pdf', '.xlsx', '.csv', '.txt'],
            },
        });
        this._uppy.use(XHRUpload, {
            endpoint: this._urlsService.getUrl(this._urlsService.AI_CHAT.AGENT_FILE_UPLOAD, {
                provider: 'anthropic',
            }),
            formData: true,
            fieldName: 'files',
            bundle: true,
        });
    }
    connect() {
        this.disconnect();
        try {
            const websocketProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const baseUrl = window.location.hostname === 'localhost'
                ? 'localhost:3003'
                : process.env.AI_PROXY_BASE_URL.replace(window.location.protocol, '');
            const socket = new WebSocket(`${websocketProtocol}//${baseUrl}/agent/new?provider=anthropic&thinking_effort=low`);
            socket.onopen = () => {
                this._ngZone.run(() => {
                    this.isConnected.set(true);
                });
            };
            socket.onmessage = (message) => {
                try {
                    const event = JSON.parse(message.data);
                    this._handleEvent(event);
                }
                catch (err) {
                    YChartsSentryUtils.captureException(err);
                }
            };
            socket.onerror = () => { };
            socket.onclose = () => {
                this._ngZone.run(() => {
                    this.isConnected.set(false);
                });
            };
            this._socket = socket;
        }
        catch {
            this._socket = null;
        }
    }
    disconnect() {
        if (this._socket && this._socket.readyState === WebSocket.OPEN) {
            this._socket.close(1000, 'Disconnecting.');
        }
        this._socket = null;
        this._uppy.cancelAll();
    }
    async sendUserMessage(text, files = []) {
        if (!this._socket || this._socket.readyState !== WebSocket.OPEN) {
            return;
        }
        const messageFiles = files.map((file) => ({
            attachment_url: file.name,
            attachment_mime_type: file.type,
            external_file_id: '',
        }));
        this.messages.update((msgs) => [...msgs, { role: 'user', text, files: messageFiles }]);
        let fileContentBlocks = [];
        if (files.length) {
            this.isFileUploading.set(true);
            files.forEach((file) => {
                this._uppy.addFile({ name: file.name, type: file.type, data: file });
            });
            try {
                const result = await this._uppy.upload();
                fileContentBlocks = (result?.successful?.[0]?.response?.body?.files ?? []);
            }
            finally {
                this._uppy.cancelAll();
                this.isFileUploading.set(false);
            }
        }
        const data = [
            ...fileContentBlocks.map((f) => ({ type: f.type, file_id: f.file_id })),
            { type: 'text', text },
        ];
        this._socket.send(JSON.stringify({ type: 'user_input', data }));
        // reset the state, and then override the isStreaming and isLoading
        this._resetState();
        this.isStreaming.set(true);
        this.isLoading.set(true);
    }
    newChat() {
        this._uppy.cancelAll();
        this.messages.set([]);
        this.isFileUploading.set(false);
        this._resetState();
        if (this._socket && this._socket.readyState === WebSocket.OPEN) {
            // reconnect
            this._socket.close(1000, 'Reconnecting.');
            this._socket.addEventListener('close', () => {
                this.connect();
            }, { once: true });
        }
        else {
            this.connect();
        }
    }
    interrupt() {
        if (this._socket && this._socket.readyState === WebSocket.OPEN && this.isStreaming()) {
            this._socket.send(JSON.stringify({ type: 'interrupt' }));
        }
    }
    _handleEvent(event) {
        this._ngZone.run(() => {
            switch (event.type) {
                case 'text_delta':
                    this._handleTextDelta(event.data);
                    break;
                case 'thinking_delta':
                    this._handleThinkingDelta(event.data);
                    break;
                case 'tool_call_start':
                    this._handleToolCallStart(event.data);
                    break;
                case 'tool_call_stop':
                    this._handleToolCallStop(event.data);
                    break;
                case 'done':
                    this._handleDone(event.data);
                    break;
                case 'error':
                    this._handleError(event.data);
                    break;
                case 'requesting_confirmation':
                    this._handleRequestingConfirmation(event.data);
                    break;
                case 'interrupted':
                    this._handleInterrupted();
                    break;
                default:
                    break;
            }
        });
    }
    _handleThinkingDelta(text) {
        this._currentThinkingText += text;
        const thinkingText = this._currentThinkingText;
        this.agentSteps.update((steps) => {
            const last = steps[steps.length - 1];
            if (last && last.type === 'thinking') {
                return [...steps.slice(0, -1), { ...last, description: thinkingText }];
            }
            return [...steps, { type: 'thinking', title: 'Thinking', description: thinkingText }];
        });
        this.currentLoadingText.set('Thinking...');
        this.isLoading.set(true);
    }
    _handleTextDelta(text) {
        this._currentThinkingText = '';
        const isNewMessage = this._currentAssistantText === '';
        this._currentAssistantText += text;
        const currentText = this._currentAssistantText;
        if (isNewMessage) {
            const currentSteps = this.agentSteps();
            const steps = currentSteps.length ? [...currentSteps] : undefined;
            this.agentSteps.set([]);
            this.isLoading.set(false);
            this.messages.update((msgs) => [...msgs, { role: 'assistant', text: currentText, files: [], steps }]);
        }
        else {
            this.messages.update((msgs) => {
                const last = msgs[msgs.length - 1];
                if (last && last.role === 'assistant') {
                    return [...msgs.slice(0, -1), { ...last, text: currentText }];
                }
                return msgs;
            });
        }
    }
    _handleToolCallStart(data) {
        this._currentThinkingText = '';
        if (this._currentAssistantText) {
            this._currentAssistantText = '';
        }
        const step = {
            type: 'tool_call',
            title: data.name,
            description: data.display_status ?? '',
        };
        if (data.display_status) {
            this.currentLoadingText.set(data.display_status);
        }
        this.agentSteps.update((steps) => [...steps, step]);
        this.isLoading.set(true);
    }
    _handleToolCallStop(data) {
        const updateStep = (step) => step.type === 'tool_call' && step.title === data.name
            ? { ...step, description: data.display_status ?? step.description }
            : step;
        this.agentSteps.update((steps) => steps.map(updateStep));
    }
    _handleDone(displayActions) {
        const pendingConfirmations = this.toolCallsToConfirm();
        if (!pendingConfirmations.length) {
            const currentSteps = this.agentSteps();
            if (currentSteps.length && !this._currentAssistantText) {
                this.messages.update((msgs) => [
                    ...msgs,
                    { role: 'assistant', text: '', files: [], steps: [...currentSteps] },
                ]);
            }
        }
        if (this._currentAssistantText || displayActions?.length) {
            const actions = displayActions?.length ? displayActions : undefined;
            this.messages.update((msgs) => {
                const last = msgs[msgs.length - 1];
                if (last && last.role === 'assistant') {
                    return [...msgs.slice(0, -1), { ...last, displayActions: actions }];
                }
                return msgs;
            });
        }
        // Preserve pending confirmations and accumulated steps across the done event
        // since requesting_confirmation is typically followed by done before the user answers.
        const pendingSteps = pendingConfirmations.length ? [...this.agentSteps()] : [];
        this._resetState();
        this.toolCallsToConfirm.set(pendingConfirmations);
        this.agentSteps.set(pendingSteps);
    }
    _handleError(errorText) {
        this.messages.update((msgs) => [...msgs, { role: 'assistant', text: `Error: ${errorText}`, files: [] }]);
        this._resetState();
    }
    _handleRequestingConfirmation(data) {
        this.toolCallsToConfirm.set(data);
        this.isLoading.set(false);
    }
    confirmToolCall(authorize, alwaysApply, toolCall) {
        if (!this._socket || this._socket.readyState !== WebSocket.OPEN) {
            return;
        }
        let answer;
        if (alwaysApply) {
            answer = authorize ? 'allow_always' : 'reject';
        }
        else {
            answer = authorize ? 'allow' : 'reject';
        }
        if (alwaysApply && authorize) {
            this._socket.send(JSON.stringify({
                type: 'tool_permission_update',
                data: { name: toolCall.name, permission: 'allowed' },
            }));
        }
        if (alwaysApply && !authorize) {
            this._socket.send(JSON.stringify({
                type: 'tool_permission_update',
                data: { name: toolCall.name, permission: 'disabled' },
            }));
        }
        this._socket.send(JSON.stringify({
            type: 'user_confirmation',
            data: [{ tool_use_id: toolCall.tool_use_id, answer }],
        }));
        this.toolCallsToConfirm.update((calls) => calls.filter((tc) => tc.tool_use_id !== toolCall.tool_use_id));
        if (!this.toolCallsToConfirm().length) {
            this.isStreaming.set(true);
            this.isLoading.set(true);
        }
    }
    _handleInterrupted() {
        this._resetState();
    }
    _resetState() {
        this._currentAssistantText = '';
        this._currentThinkingText = '';
        this.isStreaming.set(false);
        this.isLoading.set(false);
        this.currentLoadingText.set(DEFAULT_LOADING_TEXT);
        this.agentSteps.set([]);
        this.toolCallsToConfirm.set([]);
    }
}
AgentWebSocketService.ɵfac = function AgentWebSocketService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AgentWebSocketService)(); };
AgentWebSocketService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AgentWebSocketService, factory: AgentWebSocketService.ɵfac, providedIn: 'root' });
